"use client";

import Image from "next/image";

const BARCODE_BARS = [
  3, 1, 2, 1, 3, 2, 1, 2, 3, 1, 2, 1, 3, 2, 1, 3, 1, 2, 1, 2,
] as const;

export default function NotFoundBadge() {
  return (
    <div className="pointer-events-none relative z-10 -mt-[520px] select-none">
      <style>{`
        @keyframes nfb-dangle {
          0%, 100% { transform: rotate(-11deg); }
          50% { transform: rotate(-6deg); }
        }
        @keyframes nfb-fray {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(0.5px, 1px) rotate(3deg); }
        }
        @keyframes nfb-stamp-in {
          0% { transform: rotate(-14deg) scale(0.6); opacity: 0; }
          70% { transform: rotate(-14deg) scale(1.08); opacity: 1; }
          100% { transform: rotate(-14deg) scale(1); opacity: 1; }
        }
        .nfb-pivot {
          transform-origin: 56% -90px;
          animation: nfb-dangle 4.6s cubic-bezier(0.45, 0, 0.55, 1) infinite;
        }
        .nfb-fray {
          animation: nfb-fray 1.8s ease-in-out infinite;
        }
        .nfb-stamp {
          animation: nfb-stamp-in 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both;
          animation-delay: 0.9s;
        }
        @media (prefers-reduced-motion: reduce) {
          .nfb-pivot { animation: none; transform: rotate(-8deg); }
          .nfb-fray { animation: none; }
          .nfb-stamp { animation: none; transform: rotate(-14deg); }
        }
      `}</style>

      <div className="nfb-pivot relative flex flex-col items-center">
        {/* Lanyard: left strand snapped, right strand intact */}
        <svg
          width="180"
          height="500"
          viewBox="0 0 180 500"
          fill="none"
          className="relative z-0 -mb-3 overflow-visible"
          aria-hidden="true"
          preserveAspectRatio="none"
        >
          {/* Right strand: intact, full length down to badge */}
          <path
            d="M94 0 C 96 130, 150 320, 90 500"
            stroke="#0a0a0a"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M94 0 C 96 130, 150 320, 90 500"
            stroke="rgba(255,255,255,0.16)"
            strokeWidth="0.8"
            strokeLinecap="round"
            fill="none"
            transform="translate(0.6, 0.6)"
          />

          {/* Left strand: short stub from grommet, snapped mid-air */}
          <path
            d="M86 500 C 84 470, 80 450, 72 432"
            stroke="#0a0a0a"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M86 500 C 84 470, 80 450, 72 432"
            stroke="rgba(255,255,255,0.16)"
            strokeWidth="0.8"
            strokeLinecap="round"
            fill="none"
            transform="translate(-0.6, 0.6)"
          />

          {/* Frayed end at the break */}
          <g className="nfb-fray" transform="translate(72 432)">
            <path
              d="M0 0 L -4 -6"
              stroke="#0a0a0a"
              strokeWidth="1"
              strokeLinecap="round"
            />
            <path
              d="M0 0 L -1 -8"
              stroke="#0a0a0a"
              strokeWidth="1"
              strokeLinecap="round"
            />
            <path
              d="M0 0 L 3 -6"
              stroke="#0a0a0a"
              strokeWidth="1"
              strokeLinecap="round"
            />
            <path
              d="M0 0 L 5 -3"
              stroke="#0a0a0a"
              strokeWidth="0.8"
              strokeLinecap="round"
            />
          </g>
        </svg>

        {/* Badge body */}
        <div className="relative">
          {/* Grommet; torn on the left, intact on the right */}
          <div className="absolute top-[-7px] left-1/2 z-30 h-[14px] w-[36px] -translate-x-1/2">
            <div className="border-marketing-border bg-marketing-border-light absolute inset-0 rounded-full border" />
            <div className="bg-marketing-cream-light absolute top-1/2 left-1/2 h-[6px] w-[16px] -translate-x-1/2 -translate-y-1/2 rounded-full" />
            <div className="bg-marketing-blue absolute top-1/2 right-2 h-1 w-1 -translate-y-1/2 rounded-full" />
            <div
              className="absolute top-1/2 left-2 h-1 w-1 -translate-y-1/2 rounded-full border"
              style={{
                borderColor: "var(--color-marketing-accent-red-dark)",
              }}
            />
          </div>

          <div
            className="border-marketing-border bg-marketing-cream relative w-[216px] overflow-hidden rounded-[16px] border"
            style={{
              transformOrigin: "50% 0%",
              boxShadow:
                "inset 1.5px -1.5px 3px rgba(96, 96, 96, 0.22), 4px 9px 20px rgba(0, 6, 183, 0.28)",
            }}
          >
            {/* Header strip */}
            <div className="bg-marketing-blue flex items-center justify-between px-3 py-1.5">
              <span className="text-marketing-cream font-mono text-[9px] tracking-[0.22em] uppercase">
                Expert
              </span>
              <div className="flex items-center gap-1">
                <div
                  className="h-1.5 w-1.5 rounded-full"
                  style={{
                    background: "var(--color-marketing-accent-red)",
                  }}
                />
                <span className="text-marketing-cream/90 font-mono text-[8.5px] tracking-wider uppercase">
                  Not Found
                </span>
              </div>
            </div>

            {/* Photo area: replaced with prominent 404 */}
            <div className="flex flex-col items-center px-3 pt-3 pb-2">
              <div
                className="border-marketing-border bg-marketing-cream-light relative flex h-[88px] w-[88px] items-center justify-center rounded-full border-2 border-dashed"
                aria-label="404"
              >
                <span
                  className="font-mono text-[36px] leading-none font-bold tracking-[-0.04em] tabular-nums"
                  style={{ color: "var(--color-marketing-accent-red-dark)" }}
                >
                  404
                </span>
              </div>
            </div>

            {/* Title */}
            <div className="flex flex-col items-center gap-0.5 px-3 pb-1">
              <span className="text-foreground font-mono text-[11px] leading-none font-semibold tracking-[-0.02em]">
                Page Not Found
              </span>
              <span className="text-marketing-text-muted/60 font-mono text-[8.5px] tracking-[0.05em] uppercase">
                No matching record
              </span>
            </div>

            {/* Stats: blanked out */}
            <div className="border-marketing-border-light mx-3 mt-1.5 mb-2 grid grid-cols-3 gap-1 rounded-[6px] border bg-white/40 px-1 py-1.5">
              <Stat label="YRS" value="--" />
              <Stat label="STUDIES" value="--" />
              <Stat label="RATE" value="$--" />
            </div>

            {/* Attestation row: red X marks */}
            <div className="flex items-center justify-center gap-1 px-3 pb-2">
              {["ID", "LI", "EM", "IP"].map((label) => (
                <div
                  key={label}
                  className="border-marketing-border bg-marketing-cream-light flex h-[16px] items-center justify-center gap-0.5 rounded-full border px-1.5"
                >
                  <svg width="6" height="6" viewBox="0 0 6 6" fill="none">
                    <path
                      d="M1 1 L5 5 M5 1 L1 5"
                      stroke="var(--color-marketing-accent-red)"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                  <span className="text-marketing-text-secondary font-mono text-[7px] tracking-wider">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Barcode */}
            <div className="border-marketing-border-light flex items-end gap-[1.5px] border-t px-3 py-1.5">
              {BARCODE_BARS.map((w, i) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key -- static decorative bars
                  key={i}
                  className="bg-marketing-text-muted/80"
                  style={{ width: `${w}px`, height: "12px" }}
                />
              ))}
            </div>

            {/* Footer */}
            <div className="border-marketing-border-light bg-marketing-cream-light flex items-center justify-between border-t px-3 py-1.5">
              <div className="flex items-center gap-1">
                <Image
                  src="/brand/logo.svg"
                  alt=""
                  width={10}
                  height={10}
                  draggable={false}
                />
                <span className="text-marketing-text-muted font-mono text-[8px] tracking-[0.18em] uppercase">
                  Terac
                </span>
              </div>
              <span className="text-marketing-text-secondary font-mono text-[7.5px] tracking-[0.12em]">
                TR-XXXX-NULL
              </span>
            </div>

            {/* "VOID" stamp; reinforces the big 404 without restating it */}
            <div
              className="text-marketing-accent-red-dark nfb-stamp pointer-events-none absolute top-[42px] -right-2 border-2 px-1.5 py-0.5 font-mono text-[10px] font-bold tracking-[0.22em] uppercase"
              style={{
                borderRadius: "2px",
                borderColor: "var(--color-marketing-accent-red-dark)",
                background: "rgba(255, 255, 240, 0.5)",
                transform: "rotate(-14deg)",
              }}
            >
              Void
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-marketing-text-muted/70 font-mono text-[11px] leading-none font-semibold tabular-nums">
        {value}
      </span>
      <span className="text-marketing-text-secondary mt-0.5 font-mono text-[6.5px] tracking-[0.15em] uppercase">
        {label}
      </span>
    </div>
  );
}
